import { Head } from "vite-react-ssg";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { lazy, Suspense, useState, type ReactNode } from "react";
import SampleBriefing from "../components/SampleBriefing";
import { trackEvent, trackSignupCta } from "../lib/analytics";

// Below-the-fold interactive demos — code-split so they don't weigh down the
// initial bundle. Fallbacks reserve approximate height to avoid layout shift.
const AppDemo = lazy(() => import("../components/AppDemo"));
const EmailPreview = lazy(() => import("../components/EmailPreview"));
const FrontpageDemo = lazy(() => import("../components/FrontpageDemo"));
const StackedDemo = lazy(() => import("../components/StackedDemo"));
const MoltenDemo = lazy(() => import("../components/MoltenDemo"));

function DemoFallback({ minHeight }: { minHeight: number }) {
  return (
    <div
      style={{ minHeight }}
      className="max-w-3xl mx-auto rounded-2xl border border-gray-200 bg-[#fafafa] animate-pulse"
      aria-hidden="true"
    />
  );
}
import { SIGNUP_URL, SITE_URL } from "../lib/config";
import { USE_CASES } from "../lib/useCases";

const PILLARS: { icon: string; title: string; body: ReactNode }[] = [
  {
    icon: "◆",
    title: "Your topics. Any cadence.",
    body: (
      <>
        Track anything — a company, a market, a regulation — or set up
        dedicated{" "}
        <Link
          to="/ai-competitor-monitoring/"
          className="text-brand-600 underline underline-offset-2 hover:text-brand-500"
        >
          AI competitor monitoring
        </Link>
        . Set daily, weekly, or monthly delivery per topic. Your inbox, not a
        dashboard.
      </>
    ),
  },
  {
    icon: "◈",
    title: "Synthesized, not aggregated.",
    body: "Meriana reads across the web and writes a narrative briefing. Powered by Google's Gemini with live search — not a stale index.",
  },
  {
    icon: "◉",
    title: "Trust ratings & contradiction flags.",
    body: "Every source carries a credibility score. When reporting conflicts, Meriana flags it so you know exactly where to apply your own judgment.",
  },
];

const STEPS = [
  {
    n: 1,
    title: "Name your topics",
    body: "Add anything you want to track — in plain language. A company, an industry, a regulatory theme, a person.",
  },
  {
    n: 2,
    title: "Set your cadence",
    body: "Daily for fast-moving topics. Weekly for structural trends. A different schedule per topic if you want it.",
  },
  {
    n: 3,
    title: "Read your briefing",
    body: "Meriana delivers a synthesized, cited, sentiment-marked brief. Everything is archived and searchable.",
  },
];

const ARCADE_GAMES: {
  href: string;
  title: string;
  blurb: string;
  grad: string;
}[] = [
  {
    href: "/app/stacked?src=marketing",
    title: "Stacked",
    blurb:
      "Tap to drop blocks and build the tallest tower you can. Perfect drops keep your streak alive.",
    grad: "linear-gradient(135deg,#f59e0b,#f43f5e)",
  },
  {
    href: "/app/molten?src=marketing",
    title: "Molten",
    blurb:
      "Hop up the stone lanes before the lava catches you. Grab sparks, dodge fireballs, climb the ranks.",
    grad: "linear-gradient(135deg,#e83a2a,#ff9a3c)",
  },
];

const HOME_GAMES = [
  { id: "frontpage", href: "/app/frontpage?src=marketing", title: "Frontpage", tag: "Daily news word puzzle", grad: "linear-gradient(135deg,#5457e5,#8b5cf6)" },
  { id: "stacked", href: "/app/stacked?src=marketing", title: "Stacked", tag: "Arcade tower builder", grad: "linear-gradient(135deg,#f59e0b,#f43f5e)" },
  { id: "molten", href: "/app/molten?src=marketing", title: "Molten", tag: "Arcade lava hopper", grad: "linear-gradient(135deg,#e83a2a,#ff9a3c)" },
] as const;

export default function Home() {
  // Games mount only when the visitor opens them — keeps the ad landing
  // focused and avoids loading game bundles nobody asked for.
  const [gamesOpen, setGamesOpen] = useState(false);
  return (
    <>
      <Head>
        <title>Meriana — AI News Briefings on the Topics You Track</title>
        <meta
          name="description"
          content="AI-curated intelligence briefings on the topics that matter to you. Source trust ratings, sentiment analysis, and automated delivery on your schedule."
        />
        <link rel="canonical" href={SITE_URL} />
        <meta
          name="google-site-verification"
          content="fQBVkT8j_fC1_f5-D37MJGzOuIWXp-mwQaPYE8OM3j8"
        />
        <meta
          property="og:title"
          content="Meriana — AI News Briefings on the Topics You Track"
        />
        <meta
          property="og:description"
          content="AI-curated intelligence briefings on the topics that matter to you. Source trust ratings, contradiction flags, your schedule."
        />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Meriana" />
        <meta property="og:image" content={`${SITE_URL}/opengraph-card.jpg`} />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="Meriana — Track News Topics. AI-Synthesized Briefings."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Meriana — AI News Briefings on the Topics You Track"
        />
        <meta name="twitter:image" content={`${SITE_URL}/opengraph-card.jpg`} />
        <meta
          name="twitter:image:alt"
          content="Meriana — Track News Topics. AI-Synthesized Briefings."
        />
        {/* Tells Google to show "Meriana" as the site name (not the bare URL)
            in search results, and associates the brand logo with the site. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebSite",
                "@id": `${SITE_URL}/#website`,
                url: SITE_URL,
                name: "Meriana",
                alternateName: ["meriana.app", "Meriana AI Briefings"],
                description:
                  "AI-curated intelligence briefings on the topics, companies, and markets that matter to you.",
                publisher: { "@id": `${SITE_URL}/#organization` },
              },
              {
                "@type": "Organization",
                "@id": `${SITE_URL}/#organization`,
                name: "Meriana",
                url: SITE_URL,
                logo: {
                  "@type": "ImageObject",
                  url: `${SITE_URL}/apple-touch-icon.png`,
                  width: 180,
                  height: 180,
                },
              },
            ],
          })}
        </script>
      </Head>

      <Nav pill="signup" />

      <main>
        {/* ── Hero: headline + CTA + readable example briefing ─────────── */}
        <section className="bg-gradient-to-b from-brand-50 to-[#fafafa] pt-8 sm:pt-14 pb-16 lg:pb-20 px-5 sm:px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center">
            <div className="text-center lg:text-left">
              <p className="font-sans text-xs font-semibold uppercase tracking-widest text-brand-500 mb-4">
                AI-curated news briefings
              </p>
              <h1 className="font-display text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl text-gray-900 mb-5">
                Less scrolling.
                <br />
                <span className="text-brand-600">More knowing.</span>
              </h1>
              <p className="font-sans text-lg sm:text-xl text-gray-600 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-7">
                Personalized news briefings on the topics you care about. Read
                or listen—in the app or your inbox.
              </p>
              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3 sm:gap-5">
                <a
                  href={SIGNUP_URL}
                  onClick={() => trackSignupCta("hero")}
                  data-testid="link-hero-signup"
                  className="bg-brand-500 hover:bg-brand-600 text-white font-sans font-semibold px-8 py-4 rounded-lg text-base transition-colors shadow-md shadow-brand-500/20 w-full sm:w-auto text-center"
                >
                  Create my free briefing →
                </a>
                <a
                  href="#example"
                  onClick={() => trackEvent("example_opened", { location: "hero" })}
                  data-testid="link-hero-example"
                  className="font-sans text-sm font-semibold text-gray-600 hover:text-brand-600 underline underline-offset-4 decoration-gray-300 hover:decoration-brand-300 transition-colors py-2 sm:self-center"
                >
                  See an example
                </a>
              </div>
              <p className="mt-3 font-sans text-sm text-gray-500">
                No credit card required.
              </p>
            </div>
            <div id="example" className="scroll-mt-24">
              <SampleBriefing />
            </div>
          </div>
        </section>

        {/* ── Free games strip: discoverable near the top ─────────── */}
        <section
          id="games"
          aria-labelledby="games-strip-heading"
          className="px-5 sm:px-6 py-8 bg-[#fafafa] border-t border-gray-100 scroll-mt-20"
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-4">
              <h2 id="games-strip-heading" className="font-display text-xl sm:text-2xl text-gray-900">
                Free games. Play in your browser.
              </h2>
              <Link
                to="/games/"
                onClick={() => trackEvent("games_discovery_clicked", { location: "home_strip", target: "games_landing" })}
                data-testid="link-games-landing"
                className="font-sans text-sm font-semibold text-brand-600 hover:text-brand-500"
              >
                All games →
              </Link>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {HOME_GAMES.map((g) => (
                <li key={g.id}>
                  <a
                    href={g.href}
                    onClick={() => trackEvent("game_play_clicked", { game: g.id, location: "home_strip" })}
                    data-testid={`link-play-${g.id}`}
                    className="group flex items-center gap-3 p-3 rounded-2xl border border-gray-200 bg-white hover:border-brand-300 hover:bg-brand-50 transition-colors"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl font-display text-lg font-bold text-white"
                      style={{ background: g.grad }}
                    >
                      {g.title[0]}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="font-display text-base text-gray-900 block">{g.title}</span>
                      <span className="font-sans text-xs text-gray-600 block truncate">{g.tag}</span>
                    </span>
                    <span className="flex-shrink-0 font-sans text-xs font-semibold text-brand-600 group-hover:text-brand-500 border border-brand-200 rounded-full px-3 py-1.5">
                      Play
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── Interactive demo ─────────────────────────── */}
        <section id="demo" className="py-20 px-6 bg-white scroll-mt-16 border-t border-gray-100">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="font-sans text-xs font-semibold uppercase tracking-widest text-brand-500 mb-4">
              Try it yourself
            </p>
            <h2 className="font-display text-4xl text-gray-900 mb-4">
              Build a briefing. Watch it come together.
            </h2>
            <p className="font-sans text-lg text-gray-600 leading-relaxed">
              Type any topic, choose your cadence and depth, and watch Meriana synthesize a
              live-style briefing — sentiment-marked, source-rated, and capped with a
              plain-English roundup. No signup required.
            </p>
          </div>
          <Suspense fallback={<DemoFallback minHeight={480} />}>
            <AppDemo />
          </Suspense>
        </section>

        {/* ── Email preview ─────────────────────────── */}
        <section className="py-20 px-6 bg-[#fafafa]">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="font-sans text-xs font-semibold uppercase tracking-widest text-brand-500 mb-4">
              Straight to your inbox
            </p>
            <h2 className="font-display text-4xl text-gray-900 mb-4">
              This is what lands in your inbox.
            </h2>
            <p className="font-sans text-lg text-gray-600 leading-relaxed">
              No dashboard to check, no app to open. Each briefing arrives as a
              clean, scannable email — sentiment-marked, source-rated, with a
              plain-English summary and contradiction flags. Exactly like this.
            </p>
          </div>
          <Suspense fallback={<DemoFallback minHeight={480} />}>
            <EmailPreview />
          </Suspense>
        </section>

        {/* ── Three pillars ─────────────────────────── */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="p-7 border border-gray-200 rounded-2xl bg-[#fafafa]"
              >
                <span className="text-brand-500 font-sans font-bold text-2xl block mb-4">
                  {p.icon}
                </span>
                {/* h2, not h3: these are the first headings after the page h1,
                    so h3 would skip a level (Lighthouse heading-order). */}
                <h2 className="font-display text-xl text-gray-900 mb-2">
                  {p.title}
                </h2>
                <p className="font-sans text-gray-600 leading-relaxed text-sm">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── How it works ─────────────────────────── */}
        <section id="how-it-works" className="py-20 px-6 bg-[#fafafa]">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-4xl text-gray-900 text-center mb-14">
              How it works
            </h2>
            <div className="flex flex-col md:flex-row gap-0">
              {STEPS.map((step, i) => (
                <div
                  key={i}
                  className="flex md:flex-col items-start md:items-center flex-1 gap-4 md:gap-0 pb-8 md:pb-0"
                >
                  <div className="flex items-center w-full md:mb-6">
                    <div className="flex-shrink-0 w-11 h-11 rounded-full bg-brand-500 text-white flex items-center justify-center font-sans font-bold text-base">
                      {step.n}
                    </div>
                    {i < 2 && (
                      <div className="hidden md:block flex-1 h-px bg-brand-100" />
                    )}
                  </div>
                  <div className="md:px-4 text-left md:text-center">
                    <h3 className="font-display text-lg text-gray-900 mb-1">
                      {step.title}
                    </h3>
                    <p className="font-sans text-sm text-gray-600 leading-relaxed">
                      {step.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Use cases ─────────────────────────── */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <p className="font-sans text-xs font-semibold uppercase tracking-widest text-gray-500 text-center mb-10">
              Find your use case
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {USE_CASES.map((uc) => (
                <Link
                  key={uc.href}
                  to={uc.href}
                  className="group block p-6 border border-gray-200 rounded-2xl bg-[#fafafa] hover:border-brand-300 hover:bg-brand-50 transition-colors"
                >
                  <h3 className="font-display text-lg text-gray-900 mb-2 group-hover:text-brand-600 transition-colors">
                    {uc.label}
                  </h3>
                  <p className="font-sans text-sm text-gray-600 leading-relaxed">
                    {uc.blurb}
                  </p>
                  <span className="font-sans text-sm text-brand-500 mt-4 block">
                    Learn more →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── Frontpage puzzle ─────────────────────────── */}
        <section id="frontpage" className="py-14 px-6 bg-[#fafafa] scroll-mt-16 border-t border-gray-100">
          <div className="max-w-2xl mx-auto text-center mb-8">
            <p className="font-sans text-xs font-semibold uppercase tracking-widest text-brand-500 mb-4">
              Frontpage by Meriana
            </p>
            <h2 className="font-display text-2xl text-gray-900 mb-3">
              Taking a break? The news, as a daily puzzle.
            </h2>
            <p className="font-sans text-base text-gray-600 leading-relaxed">
              Solve 3 redacted words from current news headlines with 9 shared
              guesses in regular mode. Start with 2 hints and earn more as you
              solve. Free to play, no account needed. Open the games below.
            </p>
            <Link
              to="/frontpage/"
              className="inline-block mt-4 font-sans text-sm font-semibold text-brand-600 hover:text-brand-500"
            >
              Learn more about Frontpage →
            </Link>
          </div>
          <details
            className="max-w-3xl mx-auto group/games"
            onToggle={(e) => {
              const open = (e.currentTarget as HTMLDetailsElement).open;
              setGamesOpen(open);
              if (open) trackEvent("games_discovery_clicked", { location: "home_games_toggle", target: "inline_games" });
            }}
          >
            <summary
              data-testid="button-toggle-games"
              className="cursor-pointer list-none mx-auto w-fit font-sans text-sm font-semibold text-brand-600 hover:text-brand-500 border border-gray-300 hover:border-brand-300 rounded-full px-5 py-2.5 bg-white"
            >
              <span className="group-open/games:hidden">Play Frontpage and arcade games ↓</span>
              <span className="hidden group-open/games:inline">Hide games ↑</span>
            </summary>
            {gamesOpen && (
              <div className="mt-10">
            <Suspense fallback={<DemoFallback minHeight={480} />}>
              <FrontpageDemo />
            </Suspense>

            {/* Arcade games */}
            <div id="arcade" className="max-w-3xl mx-auto mt-16 scroll-mt-24">
              <p className="font-sans text-xs font-semibold uppercase tracking-widest text-gray-500 text-center mb-6">
                More games in the Meriana arcade
              </p>
              <p className="font-sans text-sm text-gray-600 text-center -mt-2 mb-6">
                Click a game to play — free, with a global leaderboard.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {ARCADE_GAMES.map((g) => (
                  <div
                    key={g.title}
                    className="border border-gray-200 rounded-2xl bg-white p-4"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        aria-hidden="true"
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-lg font-bold text-white"
                        style={{ background: g.grad }}
                      >
                        {g.title[0]}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="font-display text-lg text-gray-900 block">
                          {g.title}
                        </span>
                        <span className="font-sans text-xs text-gray-600 leading-snug block">
                          {g.blurb}
                        </span>
                      </span>
                      <a
                        href={g.href}
                        className="hidden sm:block flex-shrink-0 font-sans text-xs font-semibold text-brand-600 hover:text-brand-500"
                      >
                        Full game →
                      </a>
                    </div>
                    <div className="relative">
                      <Suspense fallback={<DemoFallback minHeight={380} />}>
                        {g.title === "Stacked" ? <StackedDemo /> : <MoltenDemo />}
                      </Suspense>
                      <a
                        href={g.href}
                        aria-label={`Play ${g.title} — opens the full game`}
                        className="absolute inset-0 z-10 flex items-end justify-center rounded-2xl group"
                      >
                        <span className="mb-4 font-sans text-xs font-bold uppercase tracking-[0.15em] text-white bg-black/45 group-hover:bg-brand-600 transition-colors px-4 py-2 rounded-full backdrop-blur-sm">
                          Click to play →
                        </span>
                      </a>
                    </div>
                    <p className="mt-3 font-sans text-[11px] text-gray-500 text-center">
                      Full {g.title} — endless levels, sound, and the global leaderboard.
                    </p>
                  </div>
                ))}
              </div>
              <p className="font-sans text-sm text-gray-600 text-center mt-5">
                Both free to play — with a global leaderboard.{" "}
                <a
                  href="/app/games?src=marketing"
                  className="font-semibold text-brand-600 hover:text-brand-500"
                >
                  Browse all games →
                </a>
              </p>
            </div>
              </div>
            )}
          </details>
        </section>

        {/* ── Bottom CTA ─────────────────────────── */}
        <section className="bg-brand-950 py-28 px-6 text-center">
          <div className="max-w-xl mx-auto">
            {/* brand-300 on the dark brand-950 background — brand-500 was 3.57:1 */}
            <p className="font-sans text-xs font-semibold uppercase tracking-widest text-brand-300 mb-5">
              Meriana
            </p>
            <h2 className="font-display text-5xl text-white leading-tight mb-8">
              Know what matters
              <br />
              before anyone else does.
            </h2>
            <a
              href={SIGNUP_URL}
              onClick={() => trackSignupCta("footer")}
              className="inline-block bg-brand-500 hover:bg-brand-600 text-white font-sans font-semibold px-8 py-4 rounded-lg text-base transition-colors"
            >
              Create my free briefing →
            </a>
            <p className="mt-5 font-sans text-sm text-gray-400">
              No credit card required. Refer friends and earn free months of
              Pro.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
